import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from 'util/UserContext';
import TextEditor from 'components/TextEditor';
import api from 'config/axiosConfig';
import { Impact, Scope, Issue, Influence, Suggestion } from 'util/constants';
import Modal from 'ui/Modal';
import { fetchUrlMetadata, UrlMetadata } from 'util/fetchUrlMetadata';
import debounce from 'lodash.debounce';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import InputTextArea from 'ui/InputTextArea';
import ScrollableTabsList from 'ui/ScrollableTabsList';
import IssueSearch from 'components/Issues/IssueSearch';
import InfluenceSearch from 'components/Influences/InfluenceSearch';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/InputSelect"
import { Info } from 'lucide-react';
import { parseWikipediaData } from 'util/wikiUtils';
import CoauthorSelect, { CoauthorSuggestion } from 'components/Coauthors/CoauthorSelect';
import EntityAvatar from 'components/Entities/EntityAvatar';
import EntityPrivacy from 'components/Entities/EntityPrivacy';
import { PrivacyLevel } from 'util/userUtils';

interface Props {
  entity?: Issue | Influence | null;
  entityType?: 'issue' | 'influence' | null;
  onImpactAdded: (impact: Impact) => void;
  open: boolean;
  onClose: () => void;
}

const AddImpact: React.FC<Props> = ({ entity, entityType, onImpactAdded, open, onClose }) => {
  const { currentUser, awaitingUser, setShowAuthModal } = useUser();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<string>(entityType || 'issue');
  const [referenceLink, setReferenceLink] = useState<string>('');
  const [scope, setScope] = useState<Scope | ''>('');
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [impactType, setImpactType] = useState<'impact_negative' | 'impact_positive'>('impact_negative');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState<boolean>(false);
  const [urlMetadata, setUrlMetadata] = useState<UrlMetadata | null>(null);
  const [newImpact, setNewImpact] = useState<Impact | null>(null);
  const [linkValidationMessage, setLinkValidationMessage] = useState<string>('')
  const [titleValidationMessage, setTitleValidationMessage] = useState<string>('')
  const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);
  const [impactEntityType, setImpactEntityType] = useState<'issue' | 'influence' | null>(entityType ? entityType : null);
  const [impactEntity, setImpactEntity] = useState<Issue | Influence | null>(entityType === 'issue' ? entity as Issue : entityType === 'influence' ? entity as Influence : null);
  const [coauthors, setCoauthors] = useState<CoauthorSuggestion[]>([]);
  const [privacy, setPrivacy] = useState<PrivacyLevel>(currentUser?.properties?.privacy_content_default_audience?.value || 'public');

  const tabs = [
    { label: "Issue", value: "issue" },
    { label: "Influence", value: "influence" }
  ];

  useEffect(() => {
    if (awaitingUser) {
      return;
    }
    if (currentUser && currentUser?.properties?.privacy_content_default_audience) {
      setPrivacy(currentUser?.properties?.privacy_content_default_audience.value);
    } else {
      setPrivacy('public');
    }
  }, [currentUser, awaitingUser]);

  // Debounced function to fetch metadata
  const debouncedFetchMetadata = debounce(async (referenceLink: string) => {
    if (!referenceLink) return;
    
    setIsLoadingMetadata(true);
    try {
      const metadata = await fetchUrlMetadata(referenceLink);
      if (metadata) {
        setUrlMetadata(metadata);
        // Auto-populate fields if they're empty
        if (!title && metadata.title) setTitle(metadata.title);
        if (!description && metadata.description) setDescription(metadata.description);
      }
    } catch (err) {
      console.error('Error fetching metadata:', err);
    } finally {
      setIsLoadingMetadata(false);
    }
  }, 200);

  // Effect to fetch metadata when reference link changes
  useEffect(() => {
    if (referenceLink) {
      debouncedFetchMetadata(referenceLink);
    }
    return () => {
      debouncedFetchMetadata.cancel();
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceLink]);

  const uploadReferenceImage = async (imageUrl: string, fileType: string, impactId: number) => {
    try {
        let blob;
        
        // Helper function to extract nested URL
        const extractNestedUrl = (url: string | any): string | null | any => {
          // If you need to match http:// as well, change this to /https?:\/\//g
          const pattern = /https?:\/\//g;
          const matches = [...url.matchAll(pattern)];
        
          // Only proceed if we have more than one "https://" match
          if (matches.length > 0) {
            // Get the index of the last occurrence
            const lastIndex = matches[matches.length - 1].index!;
            // Get everything from that index onward
            const lastUrlPortion = url.slice(lastIndex);
            // Remove any query parameters (? or & and everything after)
            const cleanedUrl = lastUrlPortion.split(/[?&]/)[0];
            return cleanedUrl;
          }
        
          return null;
        };

        // First try: Direct fetch with original URL
        try {
            const imageResponse = await fetch(imageUrl);
            blob = await imageResponse.blob();
        } catch (firstFetchError) {
            //console.log('First direct fetch failed, checking for nested URL...', firstFetchError);
            
            // Second try: Direct fetch with nested URL if it exists
            const nestedUrl = extractNestedUrl(imageUrl);
            if (nestedUrl) {
                try {
                    //console.log('Attempting fetch with nested URL:', nestedUrl);
                    const nestedResponse = await fetch(nestedUrl);
                    blob = await nestedResponse.blob();
                } catch (nestedFetchError) {
                    //console.log('Nested URL fetch failed, trying proxy...', nestedFetchError);
                    try {
                    // Third try: Through proxy
                    const proxyResponse = await api.get('/api/utilities/image/proxy', {
                        params: { imageUrl }, // Use the nested URL for proxy
                        responseType: 'blob'
                        });
                        blob = proxyResponse.data;
                    } catch (proxyError) {
                        //console.log('Proxy fetch failed, trying original URL...', proxyError);
                        const proxyResponse = await api.get('/api/utilities/image/proxy', {
                          params: { imageUrl: nestedUrl }, // Use the nested URL for proxy
                          responseType: 'blob'
                          });
                          blob = proxyResponse.data;
                    }
                }
            } else {
                //console.log('No nested URL found, trying proxy with original URL...');
                
                // If no nested URL, try proxy with original URL
                const proxyResponse = await api.get('/api/utilities/image/proxy', {
                    params: { imageUrl },
                    responseType: 'blob'
                });
                blob = proxyResponse.data;
            }
        }

        if (blob) {
            // Create form data
            const formData = new FormData();
            formData.append('image', blob, `reference_image.${fileType}`);

            // Upload to our backend
            const response = await api.post(`/api/impacts/image/${impactId}`, formData);
            return response.data.url;
        }
        return null;
    } catch (err) {
        console.error('Error uploading reference image:', err);
        return null;
    }
  };

  const uploadIssueImage = async (imageUrl: string, issueId: number) => {
      if (!imageUrl) return null;
      
      const imageResponse = await fetch(imageUrl);
      
      let contentType = imageResponse.headers.get('content-type');

      if (!contentType) {
        contentType = 'image/jpeg';
      }
      if (contentType.includes('image/svg+xml')) {
        contentType = 'image/svg';
      }
      if (contentType.includes('image/jpeg')) {
        contentType = 'image/jpg';
      }

      const blob = await imageResponse.blob();
          
      if (blob) {
          try {
              const formData = new FormData();
              formData.append('image', blob, `reference_image.${contentType?.split('/')[1].replace('jpeg', 'jpg')}`);
              const response = await api.post(`/api/issues/image/${issueId}`, formData);
              return response.data.url;
          } catch (err) {
              console.error('Error uploading reference image:', err);
              return null;
          }
      }
  };

  const handleAddImpact = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    if (!title.trim() || !referenceLink.trim() || !scope) {
      setError('All fields are required.');
      setSuccess('');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');
    setLinkValidationMessage('');
    setTitleValidationMessage('');

    try {
      let standardizedLink = referenceLink.trim();
      if (!standardizedLink.startsWith('https://') && !standardizedLink.startsWith('http://')) {
        standardizedLink = standardizedLink.replace(/^(.*?:\/\/)?/, 'https://');
      }

      // Prepare properties object
      const properties: Record<string, any> = {
        reference_link_title: urlMetadata?.title || title,
        reference_link_description: urlMetadata?.description,
        reference_link_siteName: urlMetadata?.siteName,
        reference_link_image_url: urlMetadata?.image?.[0]?.url,
        reference_link_image_type: urlMetadata?.image?.[0]?.type,
        privacy: privacy
      };

      const impactData = {
        reference_link: standardizedLink,
        scope,
        description: description.trim(),
        title: title.trim(),
        rel_type: impactType,
        user_id: currentUser.id,
        properties
      };
      
      let entityToLink = impactEntity;

      // If it's a wiki suggestion, create the issue first
      if (impactEntityType === 'issue' && (impactEntity as any).type === 'wiki') {
        const wikiImg = await parseWikipediaData((impactEntity as Issue).cname, true);
        try {
          const response = await api.post(`/api/issues`, {
            subject_cname: (impactEntity as any).cname,
            subject_properties: {
              ...(impactEntity as any).subject_properties,
              wiki_parse_first_img: wikiImg.wiki_parse_first_img
            },
            wikipedia_link: (impactEntity as any).link,
          });
          if (response.data.properties.subject_properties.wiki_parse_first_img) {
            await uploadIssueImage(response.data.properties.subject_properties.wiki_parse_first_img, response.data.id);
          }
          entityToLink = {...response.data, type: 'issue' as const};
          setImpactEntity(entityToLink);
        } catch (err: any) {
          setError('Failed to create issue. Please try again.');
          setIsSubmitting(false);
          return;
        }
      }

      const response = await api.post<Impact>(`/api/impacts/${impactEntityType}/${entityToLink?.id}`, impactData);
      const newImpact = response.data;
      //console.log(newImpact);
      //console.log(urlMetadata);
      //console.log(newImpact?.impact_id);

      // Automatically upvote the impact
      await api.post(`/api/votes/impact/${newImpact.impact_id}`, {
        user_id: newImpact.user_id,
        rel_type: 'user_upvote',
      });
      
      setNewImpact(newImpact);

      // Add coauthors if any
      if (coauthors.length > 0) {
        try {
          await api.post(`/api/coauthors/impact/${newImpact.id}/request`, {
            addedCoauthors: coauthors.map(c => ({
              id: c.id,
              type: c.type
            }))
          });
        } catch (err) {
          console.error('Error adding coauthors:', err);
          setError('Failed to add coauthors. Please try again.');
          setIsSubmitting(false);
          return;
        }
      }


      // Then handle image if present
      if (urlMetadata?.image?.[0]?.url) {
            const imageResult = await uploadReferenceImage(
                urlMetadata.image[0].url, 
                urlMetadata.image[0].type,
                newImpact.impact_id
            );
            if (!imageResult.success) {
                console.warn('Image upload failed:', imageResult.error);
            }
      }
      setSuccess('Impact added successfully!');
      onImpactAdded(newImpact);
      handleCloseModal();
      navigate(`/impact/${newImpact.impact_id}`);
    } catch (err: any) {
      if (err.response?.status === 401) {
        setShowAuthModal(true);
      } else if (err.response?.status !== 404) {
        setError('Encountered an error, but adding impact anyway.');
        setSuccess('Impact added successfully!');
        onImpactAdded(newImpact as Impact);
        handleCloseModal();
      } else {
        setError('Failed to add impact. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const isValidSubmit = () => {
      setLinkValidationMessage('');
      setTitleValidationMessage('');
      if (!impactEntity) {
        return false;
      }
      // Must have valid reference link
      if (!referenceLink || referenceLink.length === 0) {
        return false;
      }

        //1. URL must start with https:// or http://
        if (referenceLink && !referenceLink.startsWith('https://') && !referenceLink.startsWith('http://')) {
          setLinkValidationMessage('Reference URL must start with https:// or http://');
          return false;
        }

        if (!(/(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/).test(referenceLink)) {
          setLinkValidationMessage('Reference URL must be a valid URL.');
          return false;
        }

        // 3. Title must be present
        if (!title || title.length === 0) {
          setTitleValidationMessage('Title is required.');
          return false;
        }

        if (!scope) {
          return false;
        }

        // 4. No existing issue (checked via error state)
        if (error) {
          return false;
        }

        // 5. Not currently checking or submitting
        if (isLoadingMetadata || isSubmitting) {
          return false;
        }

        return true;
    };

    setSubmitEnabled(isValidSubmit());
    setLinkValidationMessage('');
    setTitleValidationMessage('');
}, [
    referenceLink,
    title,
    scope,
    error,
    isLoadingMetadata,
    isSubmitting,
    impactEntity,
    impactEntityType
]);

  const handleIssueChipClose = () => {
    setImpactEntity(null);
  };

  const handleInfluenceChipClose = () => {
    setImpactEntity(null);
  };

  const handleOnIssueSelect = (suggestion: Suggestion) => {
    setImpactEntity(suggestion as Issue);
    setImpactEntityType('issue');
  };

  const handleOnInfluenceSelect = (influence: Influence) => {
    setImpactEntity(influence);
    setImpactEntityType('influence');
  };

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    entity = null;
    setImpactEntity(null);
    setImpactEntityType(tab === 'issue' ? 'issue' : 'influence');
  };

  const handleCloseModal = () => {
    onClose();
    setReferenceLink('');
    setScope('');
    setDescription('');
    setTitle('');
    setError('');
    setSuccess('');
    setLinkValidationMessage('');
    setTitleValidationMessage('');
    setUrlMetadata(null);
    setCoauthors([]);
  };

  const handleCoauthorSelect = (suggestion: CoauthorSuggestion) => {
    if (!coauthors.some(c => c.id === suggestion.id && c.type === suggestion.type)) {
      setCoauthors([...coauthors, suggestion]);
    }
  };

  const handleCoauthorRemove = (coauthor: CoauthorSuggestion) => {
    setCoauthors(coauthors.filter(c => !(c.id === coauthor.id && c.type === coauthor.type)));
  };

  return (
    <Modal isOpen={open} onClose={handleCloseModal} title="Add impact">
      <div className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto overflow-x-hidden space-y-4">
        <div className="flex flex-col flex-grow space-y-4">
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {!entityType && `Search for an ${selectedTab}.`} Describe an impact of this {selectedTab}. Impacts can be positive or negative and must have a reference link.
          </p>
          {!entityType && (
          <ScrollableTabsList
            tabs={tabs}
            navlink={false}
            selectedTab={selectedTab}
            pageTitle="Add Impact"
            onTabChange={handleTabChange}
            className=""
          />
          )}

          <form className="flex flex-col flex-grow space-y-4">
            {/* Entity Search Field */}
            <div className="form-control mb-0">
              {selectedTab === 'issue' ? (
                <IssueSearch
                  inputType="add"
                  placeholder="Issue"
                  className="w-full"
                  fieldRequired={true}
                  prepopulateData={selectedTab === 'issue' ? entity as Issue : null}
                  validationState="none"
                  onSelect={handleOnIssueSelect}
                  onEntityChipClose={handleIssueChipClose}
                />
              ) : (
                <InfluenceSearch
                  inputType="add"
                  placeholder="Influence"
                  className="w-full"
                  fieldRequired={true}
                  prepopulateData={selectedTab === 'influence' ? entity as Influence : null}
                  validationState="none"
                  onSelect={handleOnInfluenceSelect}
                  onEntityChipClose={handleInfluenceChipClose}
                />
              )}
            </div>

            {/* Reference Link Field */}
            <div className="form-control mb-0">
              <InputTextArea
                id="impact-reference-link"
                name="impact-reference-link"
                labelText="Reference URL"
                fieldRequired
                value={referenceLink}
                onChange={(e) => setReferenceLink(e.target.value)}
                fieldPlaceholder="https://news.com/example-article"
                required
                autoComplete="url"
                spellCheck={false}
                error={linkValidationMessage}
                validationState={linkValidationMessage && referenceLink && (referenceLink.startsWith('https://') || referenceLink.startsWith('http://')) && referenceLink.length > 0 ? 'invalid' : !linkValidationMessage && referenceLink && (referenceLink.startsWith('https://') || referenceLink.startsWith('http://')) ? 'valid' : 'none'}
              />
            </div>

            {/* Title Field */}
            <div className="form-control mb-0">
              {isLoadingMetadata && !title ? (
                <LoadingSkeleton count={1} width="w-full" height="h-[60px]" margin="mx-0" className="rounded-2xl"/>
              ) : (
                <InputTextArea
                  id="impact-title"
                  name="impact-title"
                  labelText="Title"
                  fieldRequired
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  autoComplete="impact-title"
                  required
                  maxLength={300}
                  error={titleValidationMessage}
                  validationState={titleValidationMessage ? 'invalid' : !titleValidationMessage && title && title.length > 0 ? 'valid' : 'none'}
                />
              )}
            </div>

            {/* Scope Field */}
            <div className="form-control">
              <Select onValueChange={(value) => setScope(value as Scope)} required>
                <SelectTrigger className="input-select px-3 py-2">
                  <SelectValue placeholder={<><span className="text-eoe-text-meta dark:text-eoe-dark-text-meta">Scope</span><span className="text-warning-red dark:text-dark-warning-red">*</span></>}/>
                </SelectTrigger>
                <SelectContent className="z-[2000] border border-eoe-border-main dark:border-eoe-dark-border-main">
                  <SelectItem value="Local">Local</SelectItem>
                  <SelectItem value="National">National</SelectItem>
                  <SelectItem value="Global">Global</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Impact Type Field */}
            <div className="form-control">
              <Select onValueChange={(value) => setImpactType(value as 'impact_negative' | 'impact_positive')} value={impactType} required>
                <SelectTrigger className="input-select px-3 py-2">
                  <SelectValue placeholder={<><span className="text-eoe-text-meta dark:text-eoe-dark-text-meta">Impact Type</span><span className="text-warning-red dark:text-dark-warning-red">*</span></>}/>
                </SelectTrigger>
                <SelectContent className="z-[2000] border border-eoe-border-main dark:border-eoe-dark-border-main">
                  <SelectItem value="impact_negative">Negative</SelectItem>
                  <SelectItem value="impact_positive">Positive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Description Field */}
            <div className="form-control">
              <TextEditor
                value={description}
                onChange={setDescription}
                editorType="entity"
                placeholder="Description"
              />
            </div>

            {/* Coauthors Field */}
            <div className="form-control">
              <div className="flex flex-col gap-2">
                <CoauthorSelect
                  inputType="add"
                  placeholder="Add collaborators"
                  className="w-full"
                  onSelect={handleCoauthorSelect}
                  excludeIds={coauthors.map(c => c.id)}
                />
                {coauthors.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {coauthors.map((coauthor) => (
                      <EntityAvatar
                        key={`${coauthor.type}-${coauthor.id}`}
                        entity={coauthor as any}
                        entityType={coauthor.type}
                        entityChip={true}
                        showLink={false}
                        showCloseChip={true}
                        onEntityChipClose={() => handleCoauthorRemove(coauthor)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Privacy Field */}
            <div className="form-control">
              <EntityPrivacy
                privacy={privacy}
                onPrivacyChange={(newPrivacy) => setPrivacy(newPrivacy)}
                actorType="user"
                editMode={false}
              />
            </div>

            {/* Error and Success Messages */}
            {(error || success) && (
              <div className="flex items-center space-x-2">
                {error && (
                  <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                    <Info className="mr-1" />
                    <span>{error}</span>
                  </div>
                )}
                {success && (
                  <div className="flex items-center text-green-600 text-sm">
                    <Info className="mr-1" />
                    <span>{success}</span>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
          <button
            type="button"
            onClick={handleCloseModal}
            className="btn-secondary w-full sm:w-fit py-2 px-4"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleAddImpact}
            disabled={isSubmitting || !submitEnabled}
            className={`w-full sm:w-fit py-2 px-4 ${
              isSubmitting || !submitEnabled ? 'btn-primary-disabled' : 'btn-primary'
            } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddImpact;